import { createCollection } from '@rocketlane/state-db';
import { TaskAPI } from 'api/tasks';
import { isTaskPaneMiniEnabled } from './config';
import { mapTaskChangesToPayload, toCreatePayload } from './payload';
/**
 * The single source of truth for Task entities. Replaces the tasks Redux
 * slice, the React Query task caches, AgGrid row data, and the three parallel
 * optimistic systems (Redux / RQ / AgGrid nodes).
 *
 * Data enters via `push()` from: eager list loads, paginated AgGrid fetches,
 * detail fetches, and WS refetches. Mutations apply optimistically and route
 * to the correct endpoint based on which `Task` field changed.
 *
 * Callers express changes as real `Task` fields — e.g. a phase change is
 * `update(id, { projectPhase: { projectPhaseId } })`, not a flat patch. The
 * handler below translates those into the (sometimes differently-shaped) API
 * payloads.
 */ const getProjectId = (task)=>{
    var _task_project;
    return task === null || task === void 0 ? void 0 : (_task_project = task.project) === null || _task_project === void 0 ? void 0 : _task_project.projectId;
};
/**
 * Merge a partial `fields[]` patch onto the existing fields BY `fieldId`.
 *
 * A custom-field edit sends only the changed field (`{ fields: [oneField] }`).
 * A shallow `{ ...task, ...changes }` would REPLACE the whole `fields[]` with
 * that single-element array, dropping every other custom field from the
 * optimistic value (and, on an empty-body commit, from the committed value).
 * Merge by id instead: replace matching fields, append new ones, keep the rest.
 */ export const mergeTaskFields = (existing = [], incoming)=>{
    const next = existing.map((f)=>({
            ...f
        }));
    incoming.forEach((patch)=>{
        if ((patch === null || patch === void 0 ? void 0 : patch.fieldId) == null) {
            return;
        }
        const idx = next.findIndex((f)=>f.fieldId === patch.fieldId);
        if (idx >= 0) {
            next[idx] = {
                ...next[idx],
                ...patch
            };
        } else {
            next.push(patch);
        }
    });
    return next;
};
/**
 * Field-routing for task updates. Mirrors the branching previously embedded in
 * use-update-tasks.tsx and the update sagas, but in one place.
 */ const routeUpdate = async ({ id, changes, original })=>{
    var _patch_projectPhase;
    const taskId = id;
    const patch = changes !== null && changes !== void 0 ? changes : {};
    const projectId = getProjectId(original);
    // Phase change → dedicated endpoint (returns the updated Task).
    if (((_patch_projectPhase = patch.projectPhase) === null || _patch_projectPhase === void 0 ? void 0 : _patch_projectPhase.projectPhaseId) != null) {
        var _patch_startDate, _patch_dueDate;
        return TaskAPI.updatePhase(taskId, {
            projectPhase: {
                projectPhaseId: patch.projectPhase.projectPhaseId
            },
            startDate: (_patch_startDate = patch.startDate) !== null && _patch_startDate !== void 0 ? _patch_startDate : original === null || original === void 0 ? void 0 : original.startDate,
            dueDate: (_patch_dueDate = patch.dueDate) !== null && _patch_dueDate !== void 0 ? _patch_dueDate : original === null || original === void 0 ? void 0 : original.dueDate
        });
    }
    // Privacy toggle → dedicated endpoint.
    if (patch.private !== undefined) {
        return TaskAPI.updatePrivacy(taskId, {
            private: patch.private
        });
    }
    // Subtask reorder → dedicated endpoint (returns void → optimistic committed).
    if (patch.subTaskOrder !== undefined) {
        await TaskAPI.updateSubTaskOrder(taskId, {
            subTaskOrder: patch.subTaskOrder
        });
        return undefined;
    }
    // Default field update — mini vs full per the pane flag (legacy parity).
    const body = mapTaskChangesToPayload(patch);
    if (projectId == null) {
        throw new Error(`taskCollection.update: missing projectId for task ${taskId}`);
    }
    if (isTaskPaneMiniEnabled()) {
        // The mini endpoint's response is unreliable as a source of truth: it can
        // drop a just-added assignee and return a STALE `taskEffortSetting` (e.g.
        // `EQUAL_SPLIT` immediately after the user switched to custom). Committing
        // it would clobber the user's optimistic change and empty the effort table.
        // The legacy update-task saga deliberately ignored the mini response and
        // kept the existing task — match it exactly: fire the call, then return
        // undefined so the collection commits the optimistic merged value (which
        // carries the user's intent). Effort rows are derived client-side from
        // assignee + effort + setting, so no server-computed effortMeta is needed
        // to render them.
        await TaskAPI.updateTaskMini(projectId, taskId, body);
        return undefined;
    }
    return TaskAPI.updateTask(projectId, taskId, body);
};
export const taskCollection = createCollection({
    id: 'tasks',
    getKey: (task)=>task.taskId,
    // Secondary index by project so `getProjectTasksFromCollection` (and any
    // per-project live query) is O(result) instead of an O(n) `getAll().filter`
    // scan over every task in memory. `projectId` is stable (a task does not
    // change project optimistically), which is the requirement for indexing.
    indexes: {
        byProject: (task)=>{
            var _task_project;
            return (_task_project = task.project) === null || _task_project === void 0 ? void 0 : _task_project.projectId;
        },
        // Per-phase index so each swimlane subscribes to ONLY its phase's tasks: an
        // edit in phase A leaves `getByIndex('byPhase', B)` reference-equal, so
        // phase B's swimlane does not re-render. (A task's phase is stable except
        // via an explicit move, which reindexes on commit.)
        byPhase: (task)=>{
            var _task_projectPhase;
            return (_task_projectPhase = task.projectPhase) === null || _task_projectPhase === void 0 ? void 0 : _task_projectPhase.projectPhaseId;
        },
        // Per-parent index so a subtask list listens to ONLY its own subtasks (a
        // freshly-created subtask is pushed with `parentTask` set and appears
        // immediately, without the parent's `subTaskOrder` having to refresh).
        byParent: (task)=>task.parentTask
    },
    // Server payloads pushed in are authoritative; the read model has no
    // reliable monotonic version field, so always replace.
    isNewer: ()=>true,
    // Custom-field edits patch a single field; merge it into the existing
    // `fields[]` by id so the optimistic overlay keeps the other fields.
    mergeChanges: (original, changes)=>{
        const merged = {
            ...original,
            ...changes
        };
        if (changes.fields) {
            merged.fields = mergeTaskFields(original.fields, changes.fields);
        }
        return merged;
    },
    onInsert: async ({ modified })=>{
        const projectId = getProjectId(modified);
        if (projectId == null) {
            throw new Error('taskCollection.insert: missing project on new task');
        }
        // Server returns the real task; the collection swaps the temp negative id.
        return TaskAPI.createTask(projectId, toCreatePayload(modified !== null && modified !== void 0 ? modified : {}));
    },
    onUpdate: routeUpdate,
    onDelete: async ({ id, original })=>{
        const projectId = getProjectId(original);
        if (projectId == null) {
            throw new Error(`taskCollection.delete: missing projectId for task ${String(id)}`);
        }
        await TaskAPI.deleteTask(projectId, id);
    }
});
/**
 * Imperative (non-hook) read of a project's tasks from the collection.
 * Replaces `queryClient.getQueryData(getProjectTasksQueryKey(projectId))` —
 * the task-list RQ cache no longer exists. Returns api-shaped tasks.
 *
 * O(result) via the `byProject` index — not an O(n) scan over every task. This
 * matters on the edit hot path: callers re-run this on collection changes, so
 * an O(n) filter here turned each optimistic edit into a full-collection sweep.
 */ export const getProjectTasksFromCollection = (projectId)=>taskCollection.getByIndex('byProject', projectId);
/**
 * Merge tasks into the collection WITHOUT downgrading existing entries.
 *
 * Some sources (e.g. the kanban board, the paginated plan board's entity
 * pages) fetch task payloads that carry fewer fields than a full detail fetch
 * — and, with `excludeMetaDataInFields`, `fields[]` entries stripped of their
 * option metadata. A plain `push` would replace the fuller entry wholesale;
 * this overlays the incoming top-level fields onto whatever is already stored
 * AND merges `fields[]` element-wise by `fieldId` (same rule as
 * `mergeChanges`), so detail-only fields and field metadata survive. New
 * tasks are inserted as-is.
 */ export const mergeTasksIntoCollection = (tasks, opts)=>{
    if (tasks.length === 0) {
        return;
    }
    taskCollection.push(buildMergedTasks(tasks), opts);
};
/**
 * The no-downgrade merge of `mergeTasksIntoCollection`, without the push. Split
 * out so callers that must push through a DIFFERENT channel — cross-tab sync,
 * whose push carries a `source` tag that stops it echoing back — reuse the exact
 * same merge rules instead of re-deriving them.
 */ export const buildMergedTasks = (tasks)=>tasks.map((task)=>{
        // Merge onto the SYNCED value — pushing the optimistic overlay into
        // the backing store would survive a rollback as fake server truth.
        const existing = taskCollection.getSynced(task.taskId);
        if (!existing) {
            return task;
        }
        return {
            ...existing,
            ...task,
            // No-downgrade: keep the api-shaped `project` object the `byProject`
            // index reads; a store-shaped `project` (a number) would drop the task
            // out of the index.
            project: task.project && typeof task.project === 'object' ? task.project : existing.project,
            ...task.fields ? {
                fields: mergeTaskFields(existing.fields, task.fields)
            } : {
                fields: existing.fields
            }
        };
    });
